/**
 * Tovrik Kitchen Releases — channel promotion + rollback management.
 *
 * Ported from PlexaFlow/admin/src/pages/Releases.tsx (PF audit Fix 15
 * + KDS Sprint-4 F1). The page lists active + recently-active KDS OTA
 * releases per channel and lets a platform engineer:
 *   - Promote a release from alpha → beta → production
 *   - Roll back an active release (devices revert on next /check)
 *
 * Auth (S5 `kds-ota-human-auth-retire-shared-token`): the page requires a real
 * platform session — AuthGate now covers this route like every other. The
 * pasted-shared-secret panel ("run localStorage.setItem('kds_ota_admin_token',
 * …)") is DELETED: `lib/otaApi.ts` exchanges the operator's own session for a
 * ≤5-minute token (RFC 8693), so promotes and rollbacks are attributed to the
 * PERSON instead of the anonymous shared `admin`.
 *
 * A signed-in operator who lacks the `tovrik_admin` platform role gets the
 * exchange's 403 message in the error Card below — a clear refusal, not a
 * pasted-token puzzle.
 */

import { useEffect, useState } from 'react';
import { PRODUCTS } from '@tovriktech/registry';
import {
  Button,
  Card,
  EmptyState,
  Skeleton,
  StatusBadge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@tovriktech/ui';
import { otaApi, relativeTime, formatBytes, type ReleaseSummary, type Channel } from '../lib/otaApi';

const CHANNELS: Channel[] = ['production', 'beta', 'alpha'];

// Channel → the ONE platform status vocabulary (StatusBadge, rule §D).
// production = `live` (success, in customers' hands), beta = `pending`
// (in-test, teal), alpha = `draft` (earliest, neutral). A rolled-back
// release renders as `archived` (neutral, inactive).
const CHANNEL_STATUS: Record<Channel, string> = {
  production: 'live',
  beta: 'pending',
  alpha: 'draft',
};

function nextChannel(c: Channel): Channel | null {
  if (c === 'alpha') return 'beta';
  if (c === 'beta') return 'production';
  return null;
}

export function ReleasesPage() {
  const [releases, setReleases] = useState<ReleaseSummary[]>([]);
  const [filter, setFilter] = useState<Channel | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await otaApi.listReleases(filter === 'all' ? undefined : filter);
      setReleases(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load releases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handlePromote = async (release: ReleaseSummary) => {
    const next = nextChannel(release.channel);
    if (!next) return;
    if (!confirm(`Promote ${release.version} from ${release.channel} → ${next}?\n\nThis makes it the default for devices on the ${next} channel.`)) return;
    try {
      await otaApi.promote(release.version, next);
      await load();
    } catch (e) {
      alert(`Promote failed: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  };

  const handleRollback = async (release: ReleaseSummary) => {
    if (!confirm(
      `Roll back ${release.version}?\n\n` +
      `Devices on this version will revert to the previous active release ` +
      `in their channel within 30 minutes (next OTA check). This action cannot be undone.`,
    )) return;
    try {
      await otaApi.rollback(release.id);
      await load();
    } catch (e) {
      alert(`Rollback failed: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="font-tvk-serif text-2xl font-semibold tracking-tight text-tvk-fg">
            {PRODUCTS.kitchen.display} Releases
          </h2>
          <p className="mt-1 text-sm text-tvk-fg-muted font-tvk-sans">
            Promote a build alpha &rarr; beta &rarr; production, or roll one back &mdash; devices follow on their next OTA check.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as Channel | 'all')}
            className="h-10 rounded-tvk-md border border-tvk-border bg-tvk-surface px-3 text-sm text-tvk-fg font-tvk-sans focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tvk-primary focus-visible:ring-offset-2 focus-visible:ring-offset-tvk-canvas"
          >
            <option value="all">All channels</option>
            {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button variant="secondary" onClick={load} loading={loading} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Card padding="md" className="mb-4 border-tvk-danger/30 bg-tvk-danger/10">
          <p className="text-sm font-medium text-tvk-danger">Couldn&rsquo;t load releases</p>
          <p className="mt-1 text-sm text-tvk-fg">{error}</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={load} loading={loading} disabled={loading}>
            Try again
          </Button>
        </Card>
      )}

      <Table>
        <Thead>
          <Tr>
            <Th>Version</Th>
            <Th>Channel</Th>
            <Th numeric>Devices</Th>
            <Th numeric>Health</Th>
            <Th numeric>Size</Th>
            <Th>Created</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading && (
            <>
              {Array.from({ length: 4 }).map((_, i) => (
                <Tr key={`skeleton-${i}`}>
                  <Td><Skeleton height={14} width={96} /></Td>
                  <Td><Skeleton height={20} width={72} className="rounded-full" /></Td>
                  <Td numeric><Skeleton height={14} width={32} className="ml-auto" /></Td>
                  <Td numeric><Skeleton height={14} width={40} className="ml-auto" /></Td>
                  <Td numeric><Skeleton height={14} width={56} className="ml-auto" /></Td>
                  <Td><Skeleton height={14} width={64} /></Td>
                  <Td><Skeleton height={28} width={120} /></Td>
                </Tr>
              ))}
            </>
          )}
          {!loading && releases.length === 0 && (
            <Tr>
              <Td colSpan={7} className="p-0">
                <EmptyState
                  icon={
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      <path d="m3.27 6.96 8.73 5.04 8.73-5.04" />
                      <path d="M12 22.08V12" />
                    </svg>
                  }
                  title="No releases yet"
                  description={
                    <>
                      Push a <code className="rounded-tvk-sm bg-tvk-surface-muted px-1 py-0.5 font-mono">kds-vX.Y.Z</code> tag
                      to publish a build. New releases land in the alpha channel.
                    </>
                  }
                  action={
                    <Button variant="secondary" size="sm" onClick={load} loading={loading} disabled={loading}>
                      Refresh
                    </Button>
                  }
                />
              </Td>
            </Tr>
          )}
          {!loading && releases.map((r) => (
            <ReleaseRow
              key={r.id}
              release={r}
              onPromote={() => handlePromote(r)}
              onRollback={() => handleRollback(r)}
            />
          ))}
        </Tbody>
      </Table>

      <p className="mt-4 text-xs text-tvk-fg-muted">
        New releases land in <code className="rounded-tvk-sm bg-tvk-surface-muted px-1 py-0.5 font-mono">alpha</code>.
        Promote alpha &rarr; beta &rarr; production. Rollback marks a release inactive &mdash; devices revert
        on next OTA check (~30 min) plus auto-rollback on local crash.
      </p>
    </div>
  );
}

function ReleaseRow({ release, onPromote, onRollback }: {
  release: ReleaseSummary;
  onPromote: () => void;
  onRollback: () => void;
}) {
  const promotable = release.active === 1 && nextChannel(release.channel) !== null;
  const rollbackable = release.active === 1;
  const successRate = release.install_success_rate;
  const healthClass = successRate === null ? 'text-tvk-fg-muted'
    : successRate >= 0.95 ? 'text-tvk-success'
    : successRate >= 0.80 ? 'text-tvk-warning'
    : 'text-tvk-danger';
  const healthLabel = successRate === null ? '–' : `${(successRate * 100).toFixed(0)}%`;

  return (
    <Tr className={release.active === 0 ? 'bg-tvk-surface-muted opacity-60' : ''}>
      <Td className="font-mono">{release.version}</Td>
      <Td>
        {release.active === 0 ? (
          <StatusBadge status="archived">rolled-back</StatusBadge>
        ) : (
          <StatusBadge status={CHANNEL_STATUS[release.channel]}>{release.channel}</StatusBadge>
        )}
      </Td>
      <Td numeric>{release.device_count}</Td>
      <Td numeric className={healthClass}>{healthLabel}</Td>
      <Td numeric className="text-tvk-fg-muted">{formatBytes(release.bundle_size)}</Td>
      <Td className="text-tvk-fg-muted">{relativeTime(release.created_at)}</Td>
      <Td>
        <div className="flex gap-2">
          {promotable && (
            <Button variant="primary" size="sm" onClick={onPromote}>
              Promote → {nextChannel(release.channel)}
            </Button>
          )}
          {rollbackable && (
            <Button variant="danger" size="sm" onClick={onRollback}>
              Rollback
            </Button>
          )}
        </div>
      </Td>
    </Tr>
  );
}
